/**
 * Task & Middleware Service - Centralized API layer for task management
 *
 * Contains: ymsAdminClient, middleware client, task operations, notifications.
 * Auth API calls (config, OTP, verify, signup, carriers) are in AuthenticationService.ts.
 */

import axios from 'axios';

import { Endpoints, YMS_API_CONFIG } from '@/constants';
import { refreshTokenUpdate } from '@/store';
import { store } from '@/store/store';
import { formatPSTDate } from '@/utils';

import type { TransmissionHeader, NotificationResponseDTO } from '@/types';
import type { AxiosError, AxiosRequestConfig, AxiosInstance } from 'axios';

// ─────────────────────────────────────────────────────────────────
// Retry config type
// ─────────────────────────────────────────────────────────────────

interface RetryConfig extends AxiosRequestConfig {
  _retryCount?: number;
  _tokenRefreshed?: boolean;
}

// ─────────────────────────────────────────────────────────────────
// Create dedicated Axios instance for YMS Admin API
// ─────────────────────────────────────────────────────────────────

export const ymsAdminClient = axios.create({
  baseURL: YMS_API_CONFIG.adminBaseUrl,
  timeout: YMS_API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Request interceptor — attach access token from Redux store
// Xamarin sends the header as "access-Token" (AppConstants.AccessToken).
// The backend does case-sensitive lookup, so the casing must match exactly.
ymsAdminClient.interceptors.request.use((config) => {
  const token = store.getState().user.accessToken;
  if (token) {
    config.headers['access-Token'] = token;
  }
  return config;
});

// Response interceptor with 401 token refresh + retry logic
ymsAdminClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryConfig | undefined;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // 401 — attempt one token refresh then retry the original request.
    // Skip if this request was already a refresh attempt (_tokenRefreshed flag).
    if (error.response?.status === 401 && !originalRequest._tokenRefreshed) {
      originalRequest._tokenRefreshed = true;

      const currentToken = store.getState().user.accessToken;
      if (currentToken) {
        try {
          const functionCode = store.getState().global.functionCodes?.refresh_token ?? '';
          const refreshRes = await ymsAdminClient.post<{ accessToken: string }>(
            `${Endpoints.RefreshTokenAPI}${functionCode}`,
            { refresh_token: currentToken },
            { _tokenRefreshed: true } as RetryConfig
          );

          const newToken = String(refreshRes.headers['access-Token'] ?? '');
          if (newToken) {
            const timeout = parseFloat(
              store.getState().global.config?.settings.APP_SESSION_TIMEOUT ?? '0'
            );
            store.dispatch(
              refreshTokenUpdate({
                accessToken: newToken,
                expiresIn: Date.now() + timeout * 1000,
              })
            );

            originalRequest.headers = originalRequest.headers ?? {};
            (originalRequest.headers as Record<string, string>)['access-Token'] = newToken;
            return await ymsAdminClient(originalRequest);
          }
        } catch {
          return Promise.reject(error);
        }
      }
    }

    // Retry logic for network errors and 5xx errors (except 503 maintenance)
    const retryCount = originalRequest._retryCount ?? 0;
    const shouldRetry =
      retryCount < YMS_API_CONFIG.retryAttempts &&
      (!error.response || (error.response.status >= 500 && error.response.status !== 503));

    if (shouldRetry) {
      originalRequest._retryCount = retryCount + 1;
      const delay = YMS_API_CONFIG.retryDelay * Math.pow(2, retryCount);
      await new Promise((resolve) => setTimeout(resolve, delay));
      return ymsAdminClient(originalRequest);
    }

    return Promise.reject(error);
  }
);

// ─────────────────────────────────────────────────────────────────
// Middleware API Client (for Manhattan/task management endpoints)
// ─────────────────────────────────────────────────────────────────

let middlewareClient: AxiosInstance | null = null;

/**
 * Initialize the middleware client with config from startup.
 * Must be called after bootstrap completes.
 */
export function initializeMiddlewareClient(
  baseUrl: string,
  authHeaders: Record<string, string>
): void {
  middlewareClient = axios.create({
    baseURL: baseUrl,
    timeout: YMS_API_CONFIG.timeout,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...authHeaders,
    },
  });

  // Add retry interceptor
  middlewareClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RetryConfig | undefined;
      if (!originalRequest) return Promise.reject(error);

      const retryCount = originalRequest._retryCount ?? 0;
      const shouldRetry =
        retryCount < YMS_API_CONFIG.retryAttempts &&
        (!error.response || (error.response.status >= 500 && error.response.status !== 503));

      if (shouldRetry) {
        originalRequest._retryCount = retryCount + 1;
        const delay = YMS_API_CONFIG.retryDelay * Math.pow(2, retryCount);
        await new Promise((resolve) => setTimeout(resolve, delay));
        if (!middlewareClient) return Promise.reject(new Error('Client not initialized'));
        return middlewareClient(originalRequest);
      }

      return Promise.reject(error);
    }
  );

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('[YMS API] Middleware Base URL:', baseUrl);
  }
}

export function getMiddlewareClient(): AxiosInstance {
  if (!middlewareClient) {
    throw new Error('Middleware client not initialized. Call initializeMiddlewareClient first.');
  }
  return middlewareClient;
}

export function isMiddlewareClientInitialized(): boolean {
  return middlewareClient !== null;
}

// ─────────────────────────────────────────────────────────────────
// Helper: Build TransmissionHeader
// ─────────────────────────────────────────────────────────────────

export function buildTransmissionHeader(userId?: string): TransmissionHeader {
  return {
    TransmissionDateTime: new Date().toISOString(),
    OriginatingSystem: 'YMS_PWA',
    TransmissionUser: userId ?? 'SYSTEM',
    RoutingCode: '',
    MessageType: 'REQUEST',
    MessageId: `${String(Date.now())}-${Math.random().toString(36).substring(2, 9)}`,
  };
}

/**
 * Build TransmissionHeader for update-task calls (accept/start/complete/cancel).
 * Matches Xamarin YMSHelperMethods.GetTransmissionHeader() exactly.
 * TransmissionUser is always "nblyms" (AppConstants.TransmissionUser) — NOT the user's phone number.
 */
export function buildUpdateTaskTransmissionHeader(): TransmissionHeader {
  return {
    TransmissionDateTime: formatPSTDate('date'),
    OriginatingSystem: 'YMSApp',
    TransmissionUser: 'nblyms',
    RoutingCode: 'MA',
    MessageType: 'updateTasks',
    MessageId: 'NA',
  };
}

// ─────────────────────────────────────────────────────────────────
// Notifications API
// ─────────────────────────────────────────────────────────────────

// Matches Xamarin NotificationService.Notification():
// Endpoints.NotificationsAPI = "list-notifications?code={0}&plant_id={1}&limit={2}&filterBy={3}"
// Uses MiddlewareFunctionCodes.Notifications as code, Plant.OrgId as plant_id
// Sends access-Token header: .WithHeader(AppConstants.AccessToken, accessToken)
// Matches Postman collection: header key is "access-Token" (hyphen, not underscore)
export async function getNotifications(
  functionCode: string,
  orgId: string,
  accessToken: string,
  limit = 20,
  filterBy = 'All'
): Promise<NotificationResponseDTO[]> {
  const response = await ymsAdminClient.get<NotificationResponseDTO[]>(
    `${Endpoints.NotificationList}${functionCode}&plant_id=${orgId}&limit=${String(limit)}&filterBy=${filterBy}`,
    {
      headers: {
        'access-Token': accessToken,
      },
    }
  );
  return response.data;
}

// ─────────────────────────────────────────────────────────────────
// Export all API functions as a facade
// ─────────────────────────────────────────────────────────────────

export const ymsApi = {
  notifications: {
    getList: getNotifications,
  },
};
